import { createSelector } from '@reduxjs/toolkit';

import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { selectPersonalisationOptions, selectProductPrice } from 'src/redux/product/selectors';
import type { CatServiceAPIProductOptionValue } from 'src/services/cat-service-api/types/CatServiceAPIProductOption';
import { imageOptionBounds } from 'src/utils/imageOptionBounds';
import { countImagesForImageOptionLimit } from 'src/utils/imageOptionWireFormat';
import { getPersonalisationBreakdown } from './helpers/selectorHelpers';

export const selectPersonalisation = (state: ReduxApplicationState) => state.personalisation;

export const selectIsSubmitPersonalisationFormPending = (state: ReduxApplicationState) =>
  state.personalisation.isPending;

export const selectSelectedOption = (state: ReduxApplicationState, optionId: number) =>
  state.personalisation.selectedPersonalisations[optionId];

export const selectSelectedPersonalisations = (state: ReduxApplicationState) =>
  state.personalisation.selectedPersonalisations;

export const selectTrackedEvents = (state: ReduxApplicationState) =>
  state.personalisation.trackedEvents;

export const selectEditItem = (state: ReduxApplicationState) => state.personalisation.editedItem;

export const selectPersonalisationFromBasket = (state: ReduxApplicationState) => {
  const { basket, personalisation } = state;

  return (basket.basket.items || []).find(({ id }) => id === personalisation.editedItem);
};

export const selectIsPersonalisationInEditMode = createSelector(
  [selectPersonalisationFromBasket, selectEditItem],
  (basketItem, editItem) => basketItem && editItem,
);

export const selectPersonalisationOptionsBreakdown = createSelector(
  [selectSelectedPersonalisations, selectProductPrice],
  (selections, { currency }) => getPersonalisationBreakdown(selections, currency),
);

export const selectSelectedPersonalisationsTotal = createSelector(
  [selectPersonalisationOptionsBreakdown],
  ({ amountAdded, amountRemoved }) => amountAdded + amountRemoved,
);

export const selectTotalPrice = createSelector(
  [selectSelectedPersonalisationsTotal, selectProductPrice],
  (personalisationsTotalAmount, productPrice) => personalisationsTotalAmount + productPrice.amount,
);

export const selectInvalidCustomPersonalisations = createSelector(
  [selectSelectedPersonalisations],
  (selections) =>
    Object.values(selections || {})
      .flat()
      .filter(
        (value?: CatServiceAPIProductOptionValue) =>
          !!value?.isCustomInput && value?.customOptionKind !== 'image' && !value?.isValid,
      ),
);

export const selectOutstandingPersonalisations = createSelector(
  [selectSelectedPersonalisations, selectPersonalisationOptions],
  (selections, personalisationOptions) =>
    personalisationOptions.filter((opt) => {
      if (!opt.required) {
        return false;
      }
      if (opt.type === 'image') {
        const { max, min } = imageOptionBounds(opt);
        const minCount = opt.required ? Math.max(min, 1) : min;
        const n = countImagesForImageOptionLimit(selections[opt.id]?.[0]);
        return n < minCount || n > max;
      }
      return !selections[opt.id]?.length;
    }),
);

export const selectNumberOfRequiredPersonalisations = createSelector(
  [selectPersonalisationOptions],
  (options) => options.reduce((count, opt) => (opt.required ? count + 1 : count), 0),
);

export const selectAreRequiredPersonalisationsIncomplete = createSelector(
  [selectOutstandingPersonalisations, selectInvalidCustomPersonalisations],
  (outstanding, invalidCustom) => outstanding.length > 0 || invalidCustom.length > 0,
);
